/**
 * Admin Revenue Governance Dashboard
 * 
 * P1: READ-ONLY admin visibility into financial state
 * 
 * PURPOSE: Instrument panel for revenue awareness, not operations console.
 * 
 * COMPONENTS:
 * 1. Revenue Overview - Gross revenue, breakdown by type
 * 2. Credit Liability Monitoring - Issued vs consumed vs outstanding
 * 3. Reader Consumption Intelligence - Top titles, per-reader usage
 * 4. Drill-down - Aggregate → Reader → Transaction → Ledger → Title
 * 5. CSV Export - Accounting reconciliation ready
 * 
 * GUARDRAILS:
 * - Read-Only: No state mutations
 * - Internal records only: No direct Stripe queries
 * - Time-bounded views: All metrics scoped by date
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DollarSign,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Users,
  BookOpen,
  Download,
  RefreshCw,
  AlertTriangle,
  Eye,
  ChevronRight,
  BarChart3,
  PieChart,
  Loader2
} from 'lucide-react';
import { useNotification } from '@/contexts/NotificationContext';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatCurrency = (cents) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
};

const formatCredits = (credits) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(credits);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================================================
// METRIC CARD COMPONENT
// ============================================================================

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, trendLabel, variant = 'default' }) => {
  const variantStyles = {
    default: 'bg-slate-800/50 border-slate-700',
    revenue: 'bg-emerald-900/30 border-emerald-700',
    liability: 'bg-amber-900/30 border-amber-700',
    consumption: 'bg-purple-900/30 border-purple-700'
  };

  return (
    <Card className={variantStyles[variant]}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-slate-400">{title}</CardTitle>
          {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-white">{value}</div>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        {trend !== undefined && (
          <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {Math.abs(trend).toFixed(1)}% {trendLabel}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// REVENUE OVERVIEW TAB
// ============================================================================

const RevenueOverviewTab = () => {
  const { notifyError } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('monthly');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/nfm/admin/governance/revenue/overview?period_type=${periodType}`
      );
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        notifyError('Failed to load revenue data');
      }
    } catch (error) {
      console.error('Error fetching revenue:', error);
      notifyError('Error loading revenue data');
    } finally {
      setLoading(false);
    }
  }, [periodType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} className="bg-slate-800 border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Badge variant="outline" className="text-slate-400">
          {data?.period_start?.substring(0, 10)} — {data?.period_end?.substring(0, 10)}
        </Badge>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Gross Revenue"
          value={formatCurrency(data?.gross_revenue_cents || 0)}
          subtitle={`${periodType} total`}
          icon={DollarSign}
          variant="revenue"
        />
        <MetricCard
          title="Bundle Revenue"
          value={formatCurrency(data?.bundle_revenue_cents || 0)}
          subtitle={`${data?.bundle_transaction_count || 0} transactions`}
          icon={CreditCard}
        />
        <MetricCard
          title="Subscription Revenue"
          value={formatCurrency(data?.subscription_revenue_cents || 0)}
          subtitle={`${data?.subscription_transaction_count || 0} transactions`}
          icon={TrendingUp}
        />
        <MetricCard
          title="Active Paying Readers"
          value={data?.active_paying_readers || 0}
          subtitle={`${data?.new_paying_readers || 0} new this period`}
          icon={Users}
        />
      </div>

      {/* Daily Breakdown Table */}
      {data?.daily_breakdown?.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Daily Breakdown</CardTitle>
            <CardDescription className="text-slate-400">
              Revenue by day within selected period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400 text-right">Bundles</TableHead>
                  <TableHead className="text-slate-400 text-right">Subscriptions</TableHead>
                  <TableHead className="text-slate-400 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.daily_breakdown.map((day) => (
                  <TableRow key={day.date} className="border-slate-700">
                    <TableCell className="text-slate-300">{day.date}</TableCell>
                    <TableCell className="text-right text-slate-300">
                      {formatCurrency(day.bundle_revenue_cents)} ({day.bundle_count})
                    </TableCell>
                    <TableCell className="text-right text-slate-300">
                      {formatCurrency(day.subscription_revenue_cents)} ({day.subscription_count})
                    </TableCell>
                    <TableCell className="text-right font-medium text-white">
                      {formatCurrency(day.total_revenue_cents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {(!data?.daily_breakdown || data.daily_breakdown.length === 0) && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">No revenue data for this period</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============================================================================
// CREDIT LIABILITY TAB
// ============================================================================

const CreditLiabilityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nfm/admin/governance/credits/liability`);
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        notifyError('Failed to load liability data');
      }
    } catch (error) {
      console.error('Error fetching liability:', error);
      notifyError('Error loading liability data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportLiability = async () => {
    try {
      window.open(`${API_URL}/api/nfm/admin/governance/export/credit-liability`, '_blank');
      console.log('[SUCCESS] ' + 'Export started');
    } catch (error) {
      notifyError('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const liabilityRatio = data?.total_credits_issued > 0
    ? ((data?.outstanding_credits / data?.total_credits_issued) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-400">
          As of: {formatDate(data?.as_of)}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="bg-slate-800 border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportLiability} className="bg-slate-800 border-slate-700">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Liability Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Credits Issued"
          value={formatCredits(data?.total_credits_issued || 0)}
          subtitle="All-time credits allocated"
          icon={TrendingUp}
        />
        <MetricCard
          title="Total Credits Consumed"
          value={formatCredits(data?.total_credits_consumed || 0)}
          subtitle="All-time usage"
          icon={TrendingDown}
        />
        <MetricCard
          title="Outstanding Credits"
          value={formatCredits(data?.outstanding_credits || 0)}
          subtitle={`${liabilityRatio}% of issued`}
          icon={AlertTriangle}
          variant="liability"
        />
        <MetricCard
          title="Consumption Velocity"
          value={`${formatCredits(data?.consumption_velocity_daily || 0)}/day`}
          subtitle={`${formatCredits(data?.consumption_velocity_weekly || 0)}/week`}
          icon={BarChart3}
          variant="consumption"
        />
      </div>

      {/* Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Credit Sources */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-400" />
              Credit Sources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">From Bundles</span>
              <span className="text-white font-medium">{formatCredits(data?.credits_from_bundles || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">From Subscriptions</span>
              <span className="text-white font-medium">{formatCredits(data?.credits_from_subscriptions || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">From Promotions</span>
              <span className="text-white font-medium">{formatCredits(data?.credits_from_promo || 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Reader Distribution */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-400" />
              Reader Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Readers with Balance</span>
              <span className="text-white font-medium">{data?.readers_with_balance || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Average Balance</span>
              <span className="text-white font-medium">{formatCredits(data?.average_balance_per_reader || 0)} RC</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============================================================================
// READER CONSUMPTION TAB
// ============================================================================

const ReaderConsumptionTab = () => {
  const [readers, setReaders] = useState([]);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReader, setSelectedReader] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [viewMode, setViewMode] = useState('readers'); // 'readers' | 'titles'

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [readersRes, titlesRes] = await Promise.all([
        fetch(`${API_URL}/api/nfm/admin/governance/consumption/readers?limit=20`),
        fetch(`${API_URL}/api/nfm/admin/governance/consumption/titles?limit=20`)
      ]);

      if (readersRes.ok) {
        const data = await readersRes.json();
        setReaders(data.readers || []);
      }
      if (titlesRes.ok) {
        const data = await titlesRes.json();
        setTitles(data.titles || []);
      }
    } catch (error) {
      console.error('Error fetching consumption:', error);
      notifyError('Error loading consumption data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openDrilldown = async (userId) => {
    setSelectedReader(userId);
    setDrilldownLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/nfm/admin/governance/drilldown/reader/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setDrilldownData(data);
      } else {
        notifyError('Failed to load reader details');
      }
    } catch (error) {
      console.error('Error fetching drilldown:', error);
      notifyError('Error loading reader details');
    } finally {
      setDrilldownLoading(false);
    }
  };

  const exportTitleConsumption = () => {
    window.open(`${API_URL}/api/nfm/admin/governance/export/consumption-by-title`, '_blank');
    console.log('[SUCCESS] ' + 'Export started');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'readers' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('readers')}
            className={viewMode === 'readers' ? 'bg-amber-600' : 'bg-slate-800 border-slate-700'}
          >
            <Users className="w-4 h-4 mr-2" />
            By Reader
          </Button>
          <Button
            variant={viewMode === 'titles' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('titles')}
            className={viewMode === 'titles' ? 'bg-amber-600' : 'bg-slate-800 border-slate-700'}
          >
            <BookOpen className="w-4 h-4 mr-2" />
            By Title
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="bg-slate-800 border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportTitleConsumption} className="bg-slate-800 border-slate-700">
            <Download className="w-4 h-4 mr-2" />
            Export Titles
          </Button>
        </div>
      </div>

      {/* Readers View */}
      {viewMode === 'readers' && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Top Readers by Consumption</CardTitle>
            <CardDescription className="text-slate-400">
              Click a row to drill down into reader details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Reader</TableHead>
                  <TableHead className="text-slate-400">Membership</TableHead>
                  <TableHead className="text-slate-400 text-right">Consumed</TableHead>
                  <TableHead className="text-slate-400 text-right">Balance</TableHead>
                  <TableHead className="text-slate-400 text-right">Titles</TableHead>
                  <TableHead className="text-slate-400"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {readers.map((reader) => (
                  <TableRow
                    key={reader.user_id}
                    className="border-slate-700 cursor-pointer hover:bg-slate-700/50"
                    onClick={() => openDrilldown(reader.user_id)}
                  >
                    <TableCell>
                      <div>
                        <div className="text-white font-medium">{reader.name}</div>
                        <div className="text-xs text-slate-500">{reader.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`${
                          reader.membership_type === 'Subscriber'
                            ? 'bg-purple-500/20 text-purple-300'
                            : reader.membership_type === 'ALaCarte'
                            ? 'bg-amber-500/20 text-amber-300'
                            : 'bg-slate-500/20 text-slate-300'
                        }`}
                      >
                        {reader.membership_type === 'ALaCarte' ? 'A La Carte' : reader.membership_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">
                      {formatCredits(reader.total_credits_consumed)} RC
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={reader.current_balance < 0 ? 'text-red-400' : 'text-emerald-400'}>
                        {formatCredits(reader.current_balance)} RC
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">
                      {reader.titles_consumed}
                    </TableCell>
                    <TableCell>
                      <Eye className="w-4 h-4 text-slate-500" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Titles View */}
      {viewMode === 'titles' && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Top Titles by Credit Consumption</CardTitle>
            <CardDescription className="text-slate-400">
              Reader credits spent per title (attribution data)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Title</TableHead>
                  <TableHead className="text-slate-400">Author</TableHead>
                  <TableHead className="text-slate-400 text-right">Credits Consumed</TableHead>
                  <TableHead className="text-slate-400 text-right">Unique Readers</TableHead>
                  <TableHead className="text-slate-400 text-right">Sessions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {titles.map((title) => (
                  <TableRow key={title.title_id} className="border-slate-700">
                    <TableCell className="text-white font-medium">{title.title}</TableCell>
                    <TableCell className="text-slate-400">{title.author}</TableCell>
                    <TableCell className="text-right text-emerald-400">
                      {formatCredits(title.total_credits_consumed)} RC
                    </TableCell>
                    <TableCell className="text-right text-slate-300">{title.unique_readers}</TableCell>
                    <TableCell className="text-right text-slate-300">{title.total_sessions}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Reader Drilldown Dialog */}
      <Dialog open={!!selectedReader} onOpenChange={() => setSelectedReader(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Reader Details</DialogTitle>
            <DialogDescription className="text-slate-400">
              Complete financial activity for this reader
            </DialogDescription>
          </DialogHeader>

          {drilldownLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : drilldownData ? (
            <div className="space-y-6">
              {/* Reader Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-400">Name</div>
                  <div className="text-white font-medium">{drilldownData.name}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Email</div>
                  <div className="text-white font-medium">{drilldownData.email}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Membership</div>
                  <Badge className="mt-1">
                    {drilldownData.membership_type === 'ALaCarte' ? 'A La Carte' : drilldownData.membership_type}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm text-slate-400">Current Balance</div>
                  <div className={`font-medium ${drilldownData.current_balance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    {formatCredits(drilldownData.current_balance)} RC
                  </div>
                </div>
              </div>

              {/* Credit Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardContent className="pt-4">
                    <div className="text-xs text-slate-400">Issued</div>
                    <div className="text-lg font-bold text-white">{formatCredits(drilldownData.total_credits_issued)}</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardContent className="pt-4">
                    <div className="text-xs text-slate-400">Consumed</div>
                    <div className="text-lg font-bold text-white">{formatCredits(drilldownData.total_credits_consumed)}</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700/50 border-slate-600">
                  <CardContent className="pt-4">
                    <div className="text-xs text-slate-400">Transactions</div>
                    <div className="text-lg font-bold text-white">{drilldownData.transaction_count}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Transactions */}
              {drilldownData.transactions?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Financial Transactions</h4>
                  <div className="max-h-40 overflow-y-auto rounded border border-slate-700">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400 text-xs">Type</TableHead>
                          <TableHead className="text-slate-400 text-xs">Amount</TableHead>
                          <TableHead className="text-slate-400 text-xs">Status</TableHead>
                          <TableHead className="text-slate-400 text-xs">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drilldownData.transactions.slice(0, 10).map((tx, i) => (
                          <TableRow key={i} className="border-slate-700">
                            <TableCell className="text-slate-300 text-xs">{tx.type}</TableCell>
                            <TableCell className="text-emerald-400 text-xs">{formatCurrency(tx.amount_cents)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{tx.status}</Badge>
                            </TableCell>
                            <TableCell className="text-slate-400 text-xs">{formatDate(tx.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Recent Ledger Entries */}
              {drilldownData.ledger_entries?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Credit Ledger Entries</h4>
                  <div className="max-h-40 overflow-y-auto rounded border border-slate-700">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400 text-xs">Type</TableHead>
                          <TableHead className="text-slate-400 text-xs">Amount</TableHead>
                          <TableHead className="text-slate-400 text-xs">Source</TableHead>
                          <TableHead className="text-slate-400 text-xs">Balance After</TableHead>
                          <TableHead className="text-slate-400 text-xs">Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {drilldownData.ledger_entries.slice(0, 15).map((entry, i) => (
                          <TableRow key={i} className="border-slate-700">
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-xs ${entry.type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}
                              >
                                {entry.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-xs ${entry.type === 'credit' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {entry.type === 'credit' ? '+' : ''}{formatCredits(entry.amount)}
                            </TableCell>
                            <TableCell className="text-slate-400 text-xs">{entry.source || '—'}</TableCell>
                            <TableCell className="text-slate-300 text-xs">{formatCredits(entry.balance_after || 0)}</TableCell>
                            <TableCell className="text-slate-400 text-xs">{formatDate(entry.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// EXPORT TAB
// ============================================================================

const ExportTab = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const exportTransactions = () => {
    window.open(
      `${API_URL}/api/nfm/admin/governance/export/transactions?start_date=${startDate}&end_date=${endDate}`,
      '_blank'
    );
    console.log('[SUCCESS] ' + 'Transaction export started');
  };

  const exportLiability = () => {
    window.open(`${API_URL}/api/nfm/admin/governance/export/credit-liability`, '_blank');
    console.log('[SUCCESS] ' + 'Liability export started');
  };

  const exportTitleConsumption = () => {
    window.open(`${API_URL}/api/nfm/admin/governance/export/consumption-by-title`, '_blank');
    console.log('[SUCCESS] ' + 'Title consumption export started');
  };

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Download className="w-5 h-5 text-amber-400" />
            Export Center
          </CardTitle>
          <CardDescription className="text-slate-400">
            Download CSV reports for accounting reconciliation and audit
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Transaction Export */}
          <div className="p-4 border border-slate-700 rounded-lg">
            <h4 className="text-white font-medium mb-4">Financial Transactions</h4>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                />
              </div>
              <Button onClick={exportTransactions} className="bg-amber-600 hover:bg-amber-700">
                <Download className="w-4 h-4 mr-2" />
                Export Transactions
              </Button>
            </div>
          </div>

          {/* Liability Export */}
          <div className="p-4 border border-slate-700 rounded-lg">
            <h4 className="text-white font-medium mb-2">Credit Liability Snapshot</h4>
            <p className="text-sm text-slate-400 mb-4">
              Current liability by reader (outstanding credits)
            </p>
            <Button onClick={exportLiability} className="bg-amber-600 hover:bg-amber-700">
              <Download className="w-4 h-4 mr-2" />
              Export Liability
            </Button>
          </div>

          {/* Title Consumption Export */}
          <div className="p-4 border border-slate-700 rounded-lg">
            <h4 className="text-white font-medium mb-2">Title Consumption Report</h4>
            <p className="text-sm text-slate-400 mb-4">
              Credits consumed per title (author revenue attribution)
            </p>
            <Button onClick={exportTitleConsumption} className="bg-amber-600 hover:bg-amber-700">
              <Download className="w-4 h-4 mr-2" />
              Export Title Consumption
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminRevenuePage = () => {
  const { user } = useAuth();

  // Check admin access
  if (!user?.roles?.includes('admin')) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Card className="bg-slate-800 border-slate-700 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-slate-400">Admin role required to view this page.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Revenue Governance</h1>
          <p className="text-slate-400">
            Read-only financial visibility • Reader-only economic activity
          </p>
          <Badge variant="outline" className="mt-2 text-amber-400 border-amber-400">
            P1 Dashboard • No Mutations
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="revenue" className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="revenue" className="data-[state=active]:bg-amber-600">
              <DollarSign className="w-4 h-4 mr-2" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="liability" className="data-[state=active]:bg-amber-600">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Credit Liability
            </TabsTrigger>
            <TabsTrigger value="consumption" className="data-[state=active]:bg-amber-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Consumption
            </TabsTrigger>
            <TabsTrigger value="export" className="data-[state=active]:bg-amber-600">
              <Download className="w-4 h-4 mr-2" />
              Export
            </TabsTrigger>
          </TabsList>

          <TabsContent value="revenue">
            <RevenueOverviewTab />
          </TabsContent>

          <TabsContent value="liability">
            <CreditLiabilityTab />
          </TabsContent>

          <TabsContent value="consumption">
            <ReaderConsumptionTab />
          </TabsContent>

          <TabsContent value="export">
            <ExportTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminRevenuePage;
